import PropTypes from 'prop-types';

// Prop value whitelist
const gridAlignmentValues = ['start', 'end', 'center', 'stretch'];

const propTypes = {
  // Item props
  /** Amount of columns item should span -- overrides gridColumn */
  span: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.number,
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
    }),
  ]),
  /** Control horizontal size and placement of Item */
  gridColumn: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      xs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  /** Control vertical size and placement of Item */
  gridRow: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      xs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  alignSelf: PropTypes.oneOfType([
    PropTypes.oneOf(gridAlignmentValues),
    PropTypes.shape({
      initial: PropTypes.oneOf(gridAlignmentValues),
      xs: PropTypes.oneOf(gridAlignmentValues),
      sm: PropTypes.oneOf(gridAlignmentValues),
      md: PropTypes.oneOf(gridAlignmentValues),
      lg: PropTypes.oneOf(gridAlignmentValues),
    }),
  ]),
  justifySelf: PropTypes.oneOfType([
    PropTypes.oneOf(gridAlignmentValues),
    PropTypes.shape({
      initial: PropTypes.oneOf(gridAlignmentValues),
      xs: PropTypes.oneOf(gridAlignmentValues),
      sm: PropTypes.oneOf(gridAlignmentValues),
      md: PropTypes.oneOf(gridAlignmentValues),
      lg: PropTypes.oneOf(gridAlignmentValues),
    }),
  ]),
  order: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.number,
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
    }),
  ]),

  // Container props
  /** Amount of columns the container should hold  -- overrides gridTemplateColumns */
  columns: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.number,
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
    }),
  ]),
  /* Spacing between cells - expects one or two value(s) */
  gap: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      xs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  /** Declare a column template */
  gridTemplateColumns: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      initial: PropTypes.string,
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
    }),
  ]),
  /** Declare a row template */
  gridTemplateRows: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      initial: PropTypes.string,
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
    }),
  ]),
  /** Controls how the auto-placement algorithm works. */
  gridAutoFlow: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      initial: PropTypes.string,
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
    }),
  ]),
  /** Specifies the size of any auto-generated row tracks */
  gridAutoRows: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      initial: PropTypes.string,
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
    }),
  ]),
  /** Specifies the size of any auto-generated column tracks */
  gridAutoColumns: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      initial: PropTypes.string,
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
    }),
  ]),
  /** Controls the alignment of items on the Column Axis within their grid area */
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf(gridAlignmentValues),
    PropTypes.shape({
      initial: PropTypes.oneOf(gridAlignmentValues),
      xs: PropTypes.oneOf(gridAlignmentValues),
      sm: PropTypes.oneOf(gridAlignmentValues),
      md: PropTypes.oneOf(gridAlignmentValues),
      lg: PropTypes.oneOf(gridAlignmentValues),
    }),
  ]),
  /** Controls the alignment of grid on the Column Axis of the grid area */
  alignContent: PropTypes.oneOfType([
    PropTypes.oneOf(gridAlignmentValues),
    PropTypes.shape({
      initial: PropTypes.oneOf(gridAlignmentValues),
      xs: PropTypes.oneOf(gridAlignmentValues),
      sm: PropTypes.oneOf(gridAlignmentValues),
      md: PropTypes.oneOf(gridAlignmentValues),
      lg: PropTypes.oneOf(gridAlignmentValues),
    }),
  ]),
  /** Controls the alignment of items on the Row Axis within their grid area */
  justifyItems: PropTypes.oneOfType([
    PropTypes.oneOf(gridAlignmentValues),
    PropTypes.shape({
      initial: PropTypes.oneOf(gridAlignmentValues),
      xs: PropTypes.oneOf(gridAlignmentValues),
      sm: PropTypes.oneOf(gridAlignmentValues),
      md: PropTypes.oneOf(gridAlignmentValues),
      lg: PropTypes.oneOf(gridAlignmentValues),
    }),
  ]),
  /** Controls the alignment of grid on the Row Axis of their grid area */
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf(gridAlignmentValues),
    PropTypes.shape({
      initial: PropTypes.oneOf(gridAlignmentValues),
      xs: PropTypes.oneOf(gridAlignmentValues),
      sm: PropTypes.oneOf(gridAlignmentValues),
      md: PropTypes.oneOf(gridAlignmentValues),
      lg: PropTypes.oneOf(gridAlignmentValues),
    }),
  ]),

  // General props
  /* Spacing within column -- applies to padding -- expects 1 to 4 values*/
  padding: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      initial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      xs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  /* Prevent an element from exceeding a maxWidth*/
  maxWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      initial: PropTypes.string,
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
    }),
  ]),

  // Component inherent (ie: ever present) props
  /* Essentially a fall back if no other props are passed */
  itemInherent: PropTypes.shape({
    gridColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  /* Essentially a fall back if no other props are passed */
  containerInherent: PropTypes.shape({
    gridTemplateRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gridTemplateColumns: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxWidth: PropTypes.string,
    padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default propTypes;
